/**
 * Szablony wiadomości dla New Students
 */
import moment from 'moment'
import { propOr } from 'ramda'
import { getAccountNumber, getSubjectExpansion } from 'utils/messageMaps'
import { getSubjectLabelMap } from 'utils/subjects'
import {
  additionalSubjectEntryMatchesTeacher,
  getEntryForTeacher,
  getSlotsForTeacher
} from 'modules/newStudents/utils/additionalData'

const DAY_NUMBER_TO_LABEL = {
  1: 'poniedziałek',
  2: 'wtorek',
  3: 'środa',
  4: 'czwartek',
  5: 'piątek',
  6: 'sobota',
  7: 'niedziela'
}

const DAY_NAME_TO_LABEL = {
  Poniedziałek: 'poniedziałek',
  Wtorek: 'wtorek',
  Środa: 'środa',
  Czwartek: 'czwartek',
  Piątek: 'piątek',
  Sobota: 'sobota',
  Niedziela: 'niedziela'
}

export const parseAdditionalData = student => {
  try {
    const raw = propOr('', 'additionalData', student)
    if (!raw) return {}
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

export const getSubjectLabelForTeacher = (teacherSubject, additionalDataSubjects, subjectLabelMap) => {
  const fromAdditional = additionalDataSubjects?.find(s =>
    (s.subject || '').trim().toLowerCase() === (teacherSubject || '').trim().toLowerCase()
  )
  if (fromAdditional?.subjectLabel) {
    return getSubjectExpansion(fromAdditional.subjectLabel) || fromAdditional.subjectLabel
  }
  return subjectLabelMap[teacherSubject]?.long ?? getSubjectExpansion(teacherSubject) ?? teacherSubject ?? ''
}

const formatSlot = (slot, subjectPart, withSubject) => {
  if (!slot?.day) {
    return subjectPart ? `${subjectPart} (termin do ustalenia)` : ''
  }
  const dayNum = Number(slot.day)
  let dayName = ''
  if (dayNum >= 1 && dayNum <= 7) dayName = DAY_NUMBER_TO_LABEL[dayNum]
  else if (typeof slot.day === 'string' && slot.day.trim()) dayName = DAY_NAME_TO_LABEL[slot.day] ?? slot.day.toLowerCase()
  if (!dayName) return ''
  const time = (slot.time || '').trim()
  const subjectSuffix = withSubject && subjectPart ? ` (${subjectPart})` : ''
  const dayTime = time ? `${dayName} ${time}` : dayName
  return `${dayTime}${subjectSuffix}`
}

export const getSlotsFromEntry = entry => {
  if (!entry) return []
  if (entry.slots && Array.isArray(entry.slots)) return entry.slots
  if (entry.day != null || entry.time) return [{ day: entry.day ?? '', time: entry.time || '' }]
  return []
}

export const formatProposedTerms = (additionalDataSubjects, withSubject = false) => {
  const entries = (additionalDataSubjects || [])
    .filter(s => s.subject || s.subjectLabel)
    .flatMap(s => {
      const subjectPart = (s.subjectLabel || getSubjectExpansion(s.subject) || s.subject || '').trim()
      const slots = getSlotsFromEntry(s)
      if (!areTeacherSlotsComplete(slots)) return []
      return slots.map(slot => formatSlot(slot, subjectPart, withSubject)).filter(Boolean)
    })
  if (entries.length === 0) return '<UZUPEŁNIJ GODZINY I DNI ZAJĘĆ>'
  return entries.join(' i ')
}

const formatDayTime = slot => {
  if (!slot?.day) return ''
  const dayNum = Number(slot.day)
  let dayName = ''
  if (dayNum >= 1 && dayNum <= 7) dayName = DAY_NUMBER_TO_LABEL[dayNum]
  else if (typeof slot.day === 'string') dayName = DAY_NAME_TO_LABEL[slot.day] ?? slot.day.toLowerCase()
  if (!dayName) return ''
  const time = (slot.time || '').trim()
  return time ? `${dayName} od ${time}` : dayName
}

const formatDayTimeShort = slot => {
  if (!slot?.day) return ''
  const dayNum = Number(slot.day)
  let dayName = ''
  if (dayNum >= 1 && dayNum <= 7) dayName = DAY_NUMBER_TO_LABEL[dayNum]
  else if (typeof slot.day === 'string') dayName = DAY_NAME_TO_LABEL[slot.day] ?? slot.day.toLowerCase()
  if (!dayName) return ''
  const time = (slot.time || '').trim()
  return time ? `${dayName} ${time}` : dayName
}

export const getNextWeekdayDate = dayNumber => {
  if (!dayNumber || dayNumber < 1 || dayNumber > 7) return null
  const jsDay = dayNumber === 7 ? 0 : dayNumber
  const today = moment()
  let date = moment(today).day(jsDay)
  if (date.isSameOrBefore(today, 'day')) date = date.add(1, 'week')
  return date
}

const POLISH_WEEKDAY_NAME_TO_NUM = {
  Poniedziałek: 1,
  Wtorek: 2,
  Środa: 3,
  Czwartek: 4,
  Piątek: 5,
  Sobota: 6,
  Niedziela: 7
}

const getWeekdayNumberFromSlot = slot => {
  if (slot == null || slot.day === '' || slot.day == null) return null
  const n = Number(slot.day)
  if (!Number.isNaN(n) && n >= 1 && n <= 7) return n
  if (typeof slot.day === 'string') return POLISH_WEEKDAY_NAME_TO_NUM[slot.day] ?? null
  return null
}

/** Dzień tygodnia i godzina muszą być ustawione — inaczej nauczyciel nie trafia do wiadomości. */
const isSlotDayAndTimeComplete = slot =>
  Boolean((slot?.time || '').trim()) && getWeekdayNumberFromSlot(slot) != null

const areTeacherSlotsComplete = slots =>
  Array.isArray(slots) && slots.length > 0 && slots.every(isSlotDayAndTimeComplete)

const resolveFirstLessonStrFromSlots = slots => {
  const complete = (slots || []).filter(isSlotDayAndTimeComplete)
  if (!complete.length) return ''
  const scored = complete
    .map(slot => {
      const dayNum = getWeekdayNumberFromSlot(slot)
      const d = getNextWeekdayDate(dayNum)
      if (!d) return null
      const { h, m } = parseSlotTimeToHm(slot.time)
      const momentAt = d.clone().hour(h).minute(m).second(0).millisecond(0)
      return { momentAt, dayNum }
    })
    .filter(Boolean)
  if (!scored.length) return ''
  const best = scored.reduce((a, b) => (a.momentAt.isBefore(b.momentAt) ? a : b))
  const dayName = DAY_NUMBER_TO_LABEL[best.dayNum] || ''
  return `${best.momentAt.format('DD/MM')} ${best.momentAt.format('HH:mm')} (${dayName})`
}

const parseSlotTimeToHm = timeStr => {
  const t = (timeStr || '').trim()
  if (!t) return { h: 0, m: 0 }
  const parts = t.split(':')
  const h = parseInt(parts[0], 10)
  const m = parseInt(parts[1] || '0', 10)
  return { h: Number.isNaN(h) ? 0 : h, m: Number.isNaN(m) ? 0 : m }
}

/** Najwcześniejsza nadchodząca data+czas pierwszych zajęć z listy slotów (dla jednego nauczyciela). */
const computeEarliestFirstLessonShort = slots => {
  const moments = (slots || [])
    .map(slot => {
      const dayNum = getWeekdayNumberFromSlot(slot)
      if (!dayNum) return null
      const d = getNextWeekdayDate(dayNum)
      if (!d) return null
      const { h, m } = parseSlotTimeToHm(slot.time)
      return d.clone().hour(h).minute(m).second(0).millisecond(0)
    })
    .filter(Boolean)
  if (!moments.length) return ''
  const earliest = moments.reduce((a, b) => (a.isBefore(b) ? a : b))
  return `${earliest.format('DD/MM')} ${earliest.format('HH:mm')}`
}

/**
 * Buduje dane o nauczycielach z terminami (wspólne dla szablonów)
 */
export const buildTeachersWithDetails = (student, schoolSubjects, teachersList) => {
  const teachers = propOr([], 'teachers', student)
  const additionalData = parseAdditionalData(student)
  const rawAdditionalSubjects = additionalData?.subjects || []
  const subjectLabelMap = getSubjectLabelMap(schoolSubjects)

  const termsFromEntry = (entry, subjectPart) => {
    if (!entry) return subjectPart ? [subjectPart + ' (do ustalenia)'] : []
    const slots = getSlotsFromEntry(entry)
    if (!slots.length) return subjectPart ? [subjectPart + ' (do ustalenia)'] : []
    return slots.map(slot => (slot.day ? formatDayTime(slot) : subjectPart + ' (do ustalenia)')).filter(Boolean)
  }

  const termsDayTimeShortFromEntry = entry => {
    if (!entry) return []
    const slots = getSlotsFromEntry(entry)
    return slots.map(slot => formatDayTimeShort(slot)).filter(Boolean)
  }

  return teachers
    .map(teacher => {
      const fullTeacher = teachersList.find(t => t.id === teacher.id)
      const subjectLabel = getSubjectLabelForTeacher(teacher.subject, rawAdditionalSubjects, subjectLabelMap)
      const slotsForTeacher = getSlotsForTeacher(teacher, rawAdditionalSubjects, schoolSubjects)
      if (!areTeacherSlotsComplete(slotsForTeacher)) return null

      const entry = getEntryForTeacher(teacher, rawAdditionalSubjects, schoolSubjects)
      const subjectPart = (entry?.subjectLabel || getSubjectExpansion(entry?.subject || teacher.subject) || teacher.subject || '').trim()
      const termsForTeacher = termsFromEntry(entry, subjectPart)
      const termsDayTimeShort = termsDayTimeShortFromEntry(entry)
      const firstLessonShortRaw = computeEarliestFirstLessonShort(slotsForTeacher)
      const firstLessonShort = firstLessonShortRaw || '<UZUPEŁNIJ DATĘ PIERWSZYCH ZAJĘĆ>'
      const firstLessonWithWeekday =
        resolveFirstLessonStrFromSlots(slotsForTeacher) || '<UZUPEŁNIJ DATĘ I GODZINĘ>'

      return {
        name: teacher?.name ?? '',
        phone: fullTeacher?.phoneNumber || '',
        subjectLabel: subjectLabel || '<PRZEDMIOT>',
        studentRate: teacher?.studentRate ?? '',
        terms: termsForTeacher,
        termsDayTimeShort: termsDayTimeShort,
        firstLessonShort,
        firstLessonWithWeekday
      }
    })
    .filter(Boolean)
}

const getAdditionalSubjectsForTeachers = (student, schoolSubjects) => {
  const teachers = propOr([], 'teachers', student)
  const rawAdditionalSubjects = parseAdditionalData(student)?.subjects || []

  const hasTeacherForSubject = s =>
    teachers.some(t => additionalSubjectEntryMatchesTeacher(s, t, schoolSubjects))

  return rawAdditionalSubjects.filter(s => {
    if (!(s.subject || s.subjectLabel)) return false
    if (!areTeacherSlotsComplete(getSlotsFromEntry(s))) return false
    return teachers.length === 0 || hasTeacherForSubject(s)
  })
}

/**
 * 1. SZABLON KRÓTKI DLA UCZNIA
 */
export const buildShortMessageForStudent = (student, schoolSubjects, teachersList) => {
  const teachersWithDetails = buildTeachersWithDetails(student, schoolSubjects, teachersList)
  const hasMulti = teachersWithDetails.length > 1
  const additionalSubjects = getAdditionalSubjectsForTeachers(student, schoolSubjects)
  const proposedTerms = formatProposedTerms(additionalSubjects, true)

  const teachersShortBlock = teachersWithDetails.length > 0
    ? teachersWithDetails
        .map(t => {
          const terms = t.terms.length ? t.terms.join(', ') : '<UZUPEŁNIJ TERMINY>'
          return hasMulti
            ? `${t.name} (${t.subjectLabel}) – ${terms}`
            : `${t.name} – ${terms}`
        })
        .join(hasMulti ? '; ' : '')
    : proposedTerms

  const totalTermsCount = teachersWithDetails.reduce((sum, t) => sum + t.terms.length, 0)
  const termQuestion = totalTermsCount === 1 ? 'Czy ten termin będzie odpowiadać?' : 'Czy te terminy będą odpowiadać?'

  return `Dzień dobry 🙂 

Nazywam się Maja Cylkowska. Razem z kolegą Maćkiem zajmujemy się koordynowaniem grafików.
Wybraliśmy ${hasMulti ? 'osoby' : 'osobę'} z zespołu, ${hasMulti ? 'które powinny' : 'która powinna'} dobrze sprawić się w powierzonej misji.
Zajęcia ${hasMulti ? 'poprowadzą' : 'poprowadzi'}${hasMulti ? ':' : ''} ${teachersShortBlock || proposedTerms}. ${termQuestion}`
}

/**
 * 2. SZABLON DŁUGI DLA UCZNIA
 */
export const buildLongMessageForStudent = (student, schoolSubjects, teachersList) => {
  const teachersWithDetails = buildTeachersWithDetails(student, schoolSubjects, teachersList)
  const hasMulti = teachersWithDetails.length > 1
  const account = getAccountNumber(student.account || 'account1')

  const allCompleteSlotsFlat = (parseAdditionalData(student)?.subjects || []).flatMap(s =>
    getSlotsFromEntry(s).filter(isSlotDayAndTimeComplete)
  )
  const firstLessonStr =
    teachersWithDetails.length === 1
      ? teachersWithDetails[0].firstLessonWithWeekday
      : resolveFirstLessonStrFromSlots(allCompleteSlotsFlat) || '<UZUPEŁNIJ DATĘ I GODZINĘ>'

  if (hasMulti) {
    const teachersBlock = teachersWithDetails
      .map(t => {
        const rate = t.studentRate ? `${t.studentRate}zł/h` : '<UZUPEŁNIJ STAWKĘ>'
        const terms = t.terms.length ? t.terms.join(', ') : '<UZUPEŁNIJ TERMINY>'
        return `${t.name} (${t.subjectLabel}) • ${rate} • ${terms} (pierwsze zajęcia: ${t.firstLessonShort})`
      })
      .join('\n')

    const contactsBlock = teachersWithDetails
      .map(t => `${t.name} (${t.subjectLabel}): ${t.phone || '<UZUPEŁNIJ TELEFON>'}`)
      .join('\n')

    return `Bardzo się cieszę i poniżej przesyłam wszystkie informacje 🙂

Potwierdzam poniższe terminy na zajęcia online:

${teachersBlock}

Zapisuję w naszym grafiku 🙂

Proszę możliwie najszybciej napisać do nauczycieli na WhatsApp, aby złapać kontakt. Następnie nauczyciele podeślą link do spotkania.

Kontakty:

${contactsBlock}

Po zajęciach bardzo proszę o wpłatę na konto: ${account}

WAŻNE: w tytule przelewu prosimy WYŁĄCZNIE o 2 słowa: imię i nazwisko ucznia.
Odbiorca: Smart School

Jeśli chodzi o sprawy związane z finansami i organizacyjne, bardzo proszę o kontakt ze mną.
Sprawy merytoryczne najlepiej omawiać z nauczycielem.

WAŻNE: w razie konieczności odwołania/przełożenia zajęć weekendowych, proszę o kontakt z korepetytorem.

Po zajęciach proszę o wpłatę za 1 zajęcia oraz informację, jak poszło i czy zajęcia ustawić na stałe w podanych terminach.

Wtedy też przeszlibyśmy na płatności miesięczne opłacane z dołu. Po miesiącu zajęć podliczamy, ile ich się odbyło i przesyłamy kwotę do zapłacenia. Tak jest najwygodniej dla obu stron. Zatem po pierwszych zajęciach prosimy o PODANIE ADRESU EMAIL, na który mamy wysyłać miesięczne rozliczenia.

Dziękuję i pozdrawiam serdecznie :)`
  }

  const joinTermsWithI = terms => {
    if (!terms?.length) return ''
    if (terms.length === 1) return terms[0]
    if (terms.length === 2) return `${terms[0]} i ${terms[1]}`
    return `${terms.slice(0, -1).join(', ')} i ${terms[terms.length - 1]}`
  }

  const singleTeacher = teachersWithDetails[0]
  const teacherName = singleTeacher?.name || '<UZUPEŁNIJ NAUCZYCIELA>'
  const subjectLabel = singleTeacher?.subjectLabel || '<PRZEDMIOT>'
  const termLabel = singleTeacher?.terms.length === 1 ? 'proponowany termin to' : 'proponowane terminy to'
  const termsStr = singleTeacher?.terms.length ? singleTeacher.terms.join(', ') : '<UZUPEŁNIJ TERMINY>'
  const cost = singleTeacher?.studentRate ? `${singleTeacher.studentRate} zł` : '<UZUPEŁNIJ KOSZT>'
  const phone = singleTeacher?.phone || '<UZUPEŁNIJ TELEFON>'

  if (singleTeacher?.terms.length === 1) {
    return `Bardzo się cieszę i poniżej przesyłam wszystkie informacje 🙂

Pierwsze zajęcia odbędą się: ${firstLessonStr || '<UZUPEŁNIJ DATĘ I GODZINĘ>'}.

Zapisuję w naszym grafiku 🙂

Koszt za 60 min zajęć to: ${cost} (${subjectLabel})

Proszę możliwie najszybciej napisać do nauczyciela na WhatsAppie, aby złapać kontakt. ${teacherName}: ${phone}. Następnie nauczyciel podeśle link do spotkania.

Po zajęciach bardzo proszę o wpłatę na konto: ${account}

WAŻNE: w tytule przelewu prosimy WYŁĄCZNIE o 2 słowa: imię i nazwisko ucznia.
Odbiorca: Smart School

Jeśli chodzi o sprawy związane z finansami i organizacyjne, bardzo proszę o kontakt ze mną.
Sprawy merytoryczne najlepiej omawiać z nauczycielem.

WAŻNE: w razie konieczności odwołania/przełożenia zajęć weekendowych, proszę o kontakt z korepetytorem.

Po zajęciach proszę o wpłatę za 1 zajęcia oraz informację, jak poszło i czy zajęcia ustawić na stałe w podanym terminie.

Wtedy też przeszlibyśmy na płatności miesięczne opłacane z dołu. Po miesiącu zajęć podliczamy, ile ich się odbyło i przesyłamy kwotę do zapłacenia. Tak jest najwygodniej dla obu stron. Zatem po pierwszych zajęciach prosimy o PODANIE ADRESU EMAIL, na który mamy wysyłać miesięczne rozliczenia.

Dziękuję i pozdrawiam serdecznie :)`
  }

  if (singleTeacher?.terms.length > 1) {
    const termsLine = joinTermsWithI(singleTeacher.terms) || '<UZUPEŁNIJ TERMINY>'
    return `Bardzo się cieszę i poniżej przesyłam wszystkie informacje 🙂

Terminy to: ${termsLine}.

Pierwsze zajęcia odbędą się: ${firstLessonStr || '<UZUPEŁNIJ DATĘ I GODZINĘ>'}.

Zapisuję w naszym grafiku 🙂

Koszt za 60 min zajęć to: ${cost} (${subjectLabel})

Proszę możliwie najszybciej napisać do nauczyciela na WhatsAppie, aby złapać kontakt. ${teacherName}: ${phone}. Następnie nauczyciel podeśle link do spotkania.

Po zajęciach bardzo proszę o wpłatę na konto: ${account}

WAŻNE: w tytule przelewu prosimy WYŁĄCZNIE o 2 słowa: imię i nazwisko ucznia.
Odbiorca: Smart School

Jeśli chodzi o sprawy związane z finansami i organizacyjne, bardzo proszę o kontakt ze mną.
Sprawy merytoryczne najlepiej omawiać z nauczycielem.

WAŻNE: w razie konieczności odwołania/przełożenia zajęć weekendowych, proszę o kontakt z korepetytorem.

Po zajęciach proszę o wpłatę za 1 zajęcia oraz informację, jak poszło i czy zajęcia ustawić na stałe w podanych terminach.

Wtedy też przeszlibyśmy na płatności miesięczne opłacane z dołu. Po miesiącu zajęć podliczamy, ile ich się odbyło i przesyłamy kwotę do zapłacenia. Tak jest najwygodniej dla obu stron. Zatem po pierwszych zajęciach prosimy o PODANIE ADRESU EMAIL, na który mamy wysyłać miesięczne rozliczenia.

Dziękuję i pozdrawiam serdecznie :)`
  }

  return `Bardzo się cieszę i poniżej przesyłam wszystkie informacje 🙂

Zajęcia poprowadzi ${teacherName}, a ${termLabel}: ${termsStr}

Pierwsze zajęcia odbędą się: ${firstLessonStr || '<UZUPEŁNIJ DATĘ I GODZINĘ>'}.

Zapisuję w naszym grafiku :)

Koszt za 60 min zajęć to: ${cost} (${subjectLabel})

Proszę możliwie najszybciej napisać do nauczyciela na WhatsAppie, aby złapać kontakt: ${phone} Następnie nauczyciel podeśle link do spotkania. 

Po zajęciach bardzo proszę o wpłatę na konto: ${account}

WAŻNE: w tytule przelewu prosimy WYŁĄCZNIE o 2 słowa: imię i nazwisko ucznia.
Odbiorca: Smart School

Jeśli chodzi o sprawy związane z finansami i organizacyjne, bardzo proszę o kontakt ze mną.
Sprawy merytoryczne najlepiej omawiać z nauczycielem.

WAŻNE: w razie konieczności odwołania/przełożenia zajęć weekendowych, proszę o kontakt z korepetytorem.

Po zajęciach proszę o wpłatę za 1 zajęcia oraz informację, jak poszło i czy zajęcia ustawić na stałe w podanym terminie.

Wtedy też przeszlibyśmy na płatności miesięczne opłacane z dołu. Po miesiącu zajęć podliczamy, ile ich się odbyło i przesyłamy kwotę do zapłacenia. Tak jest najwygodniej dla obu stron. Zatem po pierwszych zajęciach prosimy o PODANIE ADRESU EMAIL, na który mamy wysyłać miesięczne rozliczenia.

Dziękuję i pozdrawiam serdecznie :)`
}

/**
 * 3. SZABLON DLA NAU PRZED
 */
export const buildMessageForTeacherBefore = student => {
  const name = student?.name || '<UZUPEŁNIJ NAZWĘ>'
  const comment = student?.comment || ''
  const availability = student?.availability || '<UZUPEŁNIJ DYSPOZYCYJNOŚĆ>'

  return `${name}

${comment}

Dyspozycyjność:
${availability}`
}

/**
 * 4. SZABLON DLA NAU PO USTAWIENIU
 */
export const buildMessageForTeacherAfter = (student, schoolSubjects, teachersList) => {
  const teachersWithDetails = buildTeachersWithDetails(student, schoolSubjects, teachersList)
  const hasMulti = teachersWithDetails.length > 1
  const name = student?.name || '<UZUPEŁNIJ NAZWĘ>'
  const comment = student?.comment || ''

  if (teachersWithDetails.length === 0) {
    return `Potwierdzam zajęcia:

${name}

${comment}

Termin(y): <UZUPEŁNIJ TERMINY>.

Pierwsze zajęcia odbędą się: <UZUPEŁNIJ DATĘ I GODZINĘ>

Ma odezwać się na na Whatsappie (numer ucznia/rodzica: ${student.phone || '<UZUPEŁNIJ TELEFON>'}).

Zapisane w grafiku i wpisane do panelu :)`
  }

  if (hasMulti) {
    const termsBlock = teachersWithDetails
      .map(t => `${t.name}: ${t.terms.length ? t.terms.join(', ') : '<UZUPEŁNIJ TERMINY>'}`)
      .join('\n')

    const firstLessonsBlock = teachersWithDetails
      .map(t => `${t.name} ${t.firstLessonWithWeekday}`)
      .join('\n')

    return `Potwierdzam zajęcia:

${name}

${comment}

Termin: 

${termsBlock}

Pierwsze zajęcia odbędą się: 

${firstLessonsBlock}

Zapisane w grafiku i wpisane do panelu :)`
  }

  const singleTeacher = teachersWithDetails[0]
  const termsStr = singleTeacher?.terms.length ? singleTeacher.terms.join(', ') : '<UZUPEŁNIJ TERMINY>'
  const firstLessonStr = singleTeacher?.firstLessonWithWeekday || '<UZUPEŁNIJ DATĘ I GODZINĘ>'

  return `Potwierdzam zajęcia:

${name}

${comment}

Termin(y): ${termsStr}.

Pierwsze zajęcia odbędą się: ${firstLessonStr}

Zapisane w grafiku i wpisane do panelu :)`
}
